import * as API from "../components/api/index";
import { ROUTES } from "../api/routes";
import { createContext, useState, useContext, useRef } from "react";

const DataViewContext = createContext();

export const useDataView = () => useContext(DataViewContext);

export function DataViewProvider({ children }) {
  const mainNavIndexSession = sessionStorage.getItem("mainNavIndex");
  let currentIndex = 0;
  if (mainNavIndexSession) {
    currentIndex = parseInt(mainNavIndexSession);
  }

  const subNavIndexSession = sessionStorage.getItem("subNavIndex");
  let currentSubNavIndex = 0;
  if (subNavIndexSession) {
    currentSubNavIndex = parseInt(subNavIndexSession);
  }

  let defaultNavigationIndex = {
    main: currentIndex,
    sub: currentSubNavIndex,
  };

  const { REACT_APP_ENV = "DEV" } = process.env;

  let envColumn1 = "";
  let envColumn2 = "";

  if (REACT_APP_ENV === "TEST") {
    envColumn1 = "Dev";
    envColumn2 = "Prod";
  } else if (REACT_APP_ENV === "PROD") {
    envColumn1 = "Dev";
    envColumn2 = "Test";
  } else {
    envColumn1 = "Test";
    envColumn2 = "Prod";
  }

  const [fileTransfers, setFileTransfers] = useState([]);
  const [eiConnections, setEIConnections] = useState([]);
  const [ouraPatients, setOuraPatients] = useState([]);
  const [apiConfigs, setApiConfigs] = useState([]);
  const [serviceConfigs, setServiceConfigs] = useState([]);
  const [taskSchedules, setQuerySchedules] = useState([]);
  const [remarqueEvents, setRemarqueEvents] = useState([]);
  const [errorLogReport, setErrorLogReport] = useState([]);
  const [eventsReport, setEventsReport] = useState([]);
  const [recieveDataReport, setRecieveDataReport] = useState([]);
  const [queryInfo, setQueryInfo] = useState([]);
  const [errorDetails, setErrorDetails] = useState([]);

  const [navigationIndex, setNavigationIndex] = useState(
    defaultNavigationIndex,
  );
  const [fileTransferStatsByTransID, setFileTransferStatsByTransID] = useState(
    [],
  );
  const [fileTransferStatsByGroup, setfileTransferStatsByGroup] = useState([]);
  const [fileTransferStatsByProject, setfileTransferStatsByProject] = useState(
    [],
  );
  const [fileTransferHistory, setFileTransferHistory] = useState([]);
  const [eiCacheList, setEICacheList] = useState([]);
  const [remoteExecEvents, setRemoteExecEvents] = useState([]);
  const [remoteProcessList, setRemoteProcessList] = useState([]);
  const [unifiedEventSummary, setUnifiedEventSummary] = useState([]);
  const [unifiedFileSummary, setUnifiedFileSummary] = useState([]);
  const [batchProcessSummary, setBatchProcessSummary] = useState([]);
  const [jobRunHistory, setJobRunHistory] = useState([]);

  const [openPromoteModal, setOpenPromoteModal] = useState(false);
  const [openMatchModal, setOpenMatchModal] = useState(false);
  const [matchSelectedFields, setMatchSelectedFields] = useState([]);
  // "pull" (bring the target env's values into this record) or "push"
  // (overwrite the target env's record with this one's values).
  const [matchDirection, setMatchDirection] = useState("pull");
  const [openDetailForm, setOpenDetailForm] = useState(false);
  const [editable, setEditable] = useState(false);

  let envCell = useRef(null);
  let matchCell = useRef(null);
  const curRow = useRef(null);

  const handleClickOpenDetail = ({ row }) => {
    curRow.current = row;
    setOpenDetailForm(true);
  };

  const handleDetailClose = () => {
    setOpenDetailForm(false);
    setEditable(false);
  };

  const onEditClick = () => {
    setEditable(true);
  };

  const handleClickOpen = (cell) => {
    let id = "" + cell.id;
    id = id.split("_")[1];
    cell.id = id;
    envCell.current = cell;
    setOpenPromoteModal(true);
  };

  const handleClose = () => {
    setOpenPromoteModal(false);
  };

  // Match ("pull other env's values into this record") modal — mirrors the
  // Promote modal above but is a separate open/cell pair since a row can
  // show a Promote icon in one env column and a Diff/Match icon in the other.
  const handleMatchClickOpen = (payload) => {
    matchCell.current = payload;
    // Default to every differing field selected — "Update All" is the
    // common case, unchecking individual fields is the exception.
    setMatchSelectedFields(payload.diffFields || []);
    setMatchDirection("pull");
    setOpenMatchModal(true);
  };

  const handleMatchClose = () => {
    setOpenMatchModal(false);
    setMatchSelectedFields([]);
    setMatchDirection("pull");
  };

  const fetchFileTransfers = () => {
    return API.GetDataFromAPI(ROUTES.fileTransfers).then((data) =>
      setFileTransfers(data),
    );
  };

  const fetchFileTransfersFromAllEnv = () => {
    API.GetDataFromAllEnv(ROUTES.fileTransfers).then((data) => {
      setFileTransfers(data);
    });
  };

  const fetchEIConenctions = () => {
    return API.GetDataFromAPI(ROUTES.connections).then((data) =>
      setEIConnections(data),
    );
  };

  const fetchEIConenctionsFromAllEnv = () => {
    return API.GetDataFromAllEnv(ROUTES.connections).then((data) =>
      setEIConnections(data),
    );
  };

  const fetchOuraPatients = () => {
    return API.GetDataFromAPI(ROUTES.ouraOnboard).then((data) =>
      setOuraPatients(data),
    );
  };

  const fetchAPIConfigs = () => {
    API.GetDataFromAPI(ROUTES.apiConfigs).then((data) => setApiConfigs(data));
  };

  const fetchAPIConfigsFromAllEnv = () => {
    API.GetDataFromAllEnv(ROUTES.apiConfigs).then((data) => {
      setApiConfigs(data);
    });
  };

  const fetchServiceConfigs = () => {
    return API.GetDataFromAPI(ROUTES.serviceConfigs).then((data) =>
      setServiceConfigs(data),
    );
  };

  const fetchServiceConfigsFromAllEnv = () => {
    return API.GetDataFromAllEnv(ROUTES.serviceConfigs).then((data) =>
      setServiceConfigs(data),
    );
  };

  const fetchQuerySchedules = () => {
    return API.GetDataFromAPI(ROUTES.schedules).then((data) =>
      setQuerySchedules(data),
    );
  };

  const fetchTaskSchedulesFromAllEnv = () => {
    return API.GetDataFromAllEnv(ROUTES.schedules).then((data) =>
      setQuerySchedules(data),
    );
  };

  const fetchRemarqueEvents = () => {
    return API.GetDataFromAPI(ROUTES.remarqueLastGetEvents).then((data) =>
      setRemarqueEvents(data),
    );
  };

  const fetchFileTransferStatsByTransID = () => {
    return API.GetDataFromAPI(ROUTES.fileTransferStatsByTransfer).then((data) => {
      setFileTransferStatsByTransID(data);
    });
  };

  const fetchFileTransferStatsByProject = () => {
    return API.GetDataFromAPI(ROUTES.fileTransferStatsByProject).then((data) => {
      setfileTransferStatsByProject(data);
    });
  };

  const fetchFileTransferStatsByGroup = () => {
    return API.GetDataFromAPI(ROUTES.fileTransferStatsByGroup).then((data) => {
      setfileTransferStatsByGroup(data);
    });
  };

  const fetchfileTransferHistory = () => {
    return API.GetDataFromAPI(ROUTES.fileTransferHistory).then((data) => {
      setFileTransferHistory(data);
    });
  };

  const fetchEICacheList = () => {
    return API.GetCacheListEI(ROUTES.cache.list).then((data) => {
      const cacheList = data["cache.data"];
      let names = cacheList.map((cacheItem) => {
        return {
          name: cacheItem.name,
          size: cacheItem.size,
          type: cacheItem.type,
          // The API returns the cached entries under "keys" (a bracketed,
          // comma-separated string), not "data" — there is no "data" field
          // in the response at all. Keeping the field named "keys" here so
          // it's clear what shape it actually is downstream.
          keys: cacheItem.keys,
        };
      });

      names = names.filter(function (element) {
        return element.name !== undefined;
      });

      setEICacheList(names);
    });
  };

  const fetchErrorLogReport = () => {
    return API.GetDataFromAPI(ROUTES.errorLogCount).then((data) => {
      setErrorLogReport(data);
    });
  };

  const fetchEventsReport = () => {
    return API.GetDataFromAPI(ROUTES.eventsCount).then((data) => {
      setEventsReport(data);
    });
  };

  const fetchRecieveDataReport = () => {
    return API.GetDataFromAPI(ROUTES.receiveDataCount).then((data) => {
      setRecieveDataReport(data);
    });
  };

  const fetchQueryInfo = () => {
    return API.GetDataFromAPI(ROUTES.queryInfo).then((data) => {
      setQueryInfo(data);
    });
  };

  const fetchErrorDetails = () => {
    return API.GetDataFromAPI(ROUTES.errorLogEvents).then((data) => {
      setErrorDetails(data);
    });
  };

  const fetchRemoteExecEvents = () => {
    return API.GetDataFromAPI(ROUTES.remoteExec).then((data) => {
      let events = null;
      if (JSON.stringify(data) === "[]") {
        events = [
          {
            TYPE: "Sample",
            STATUS: "Sample",
            CMD: "Sample",
            CREATED_TS: "Sample",
            COMPLETE_TS: "Sample",
            REQUEST_DATA: "Sample",
            RESULT_DATA: "Sample",
          },
        ];
      } else {
        events = data;
      }
      setRemoteExecEvents(events);
    });
  };

  const fetchRemoteProcessList = () => {
    return API.GetDataFromAPI(ROUTES.remoteScripts).then((data) => {
      let events = null;
      if (JSON.stringify(data) === "[]") {
        events = [
          {
            TYPE: "Sample",
            STATUS: "Sample",
            CMD: "Sample",
            CREATED_TS: "Sample",
            COMPLETE_TS: "Sample",
            REQUEST_DATA: "Sample",
            RESULT_DATA: "Sample",
          },
        ];
      } else {
        events = data;
      }
      setRemoteProcessList(events);
    });
  };

  const fetchUnifiedEventSummary = () => {
    return API.GetDataFromAPI(ROUTES.unifiedEventSummary).then((data) => {
      setUnifiedEventSummary(data);
    });
  };

  const fetchUnifiedFileSummary = () => {
    return API.GetDataFromAPI(ROUTES.unifiedFileSummary).then((data) => {
      setUnifiedFileSummary(data);
    });
  };

  const fetchBatchProcessSummary = () => {
    return API.GetDataFromAPI(ROUTES.batchProcessSummary).then((data) => {
      setBatchProcessSummary(data);
    });
  };

  const fetchJobRunHistory = () => {
    return API.GetDataFromAPI(ROUTES.jobRunHistory).then((data) => {
      setJobRunHistory(data);
    });
  };

  return (
    <DataViewContext.Provider
      value={{
        fileTransfers,
        setFileTransfers,
        eiConnections,
        setEIConnections,
        ouraPatients,
        setOuraPatients,
        apiConfigs,
        setApiConfigs,
        serviceConfigs,
        setServiceConfigs,
        taskSchedules,
        setQuerySchedules,
        navigationIndex,
        setNavigationIndex,
        fileTransferStatsByTransID,
        setFileTransferStatsByTransID,
        fileTransferStatsByGroup,
        setfileTransferStatsByGroup,
        fileTransferStatsByProject,
        setfileTransferStatsByProject,
        fileTransferHistory,
        setFileTransferHistory,
        eiCacheList,
        setEICacheList,
        remarqueEvents,
        setRemarqueEvents,
        errorLogReport,
        setErrorLogReport,
        eventsReport,
        setEventsReport,
        recieveDataReport,
        setRecieveDataReport,
        queryInfo,
        setQueryInfo,
        errorDetails,
        setErrorDetails,
        remoteExecEvents,
        setRemoteExecEvents,
        remoteProcessList,
        setRemoteProcessList,
        unifiedEventSummary,
        setUnifiedEventSummary,
        unifiedFileSummary,
        setUnifiedFileSummary,
        batchProcessSummary,
        setBatchProcessSummary,
        jobRunHistory,
        setJobRunHistory,
        fetchFileTransfers,
        fetchEIConenctions,
        fetchOuraPatients,
        fetchAPIConfigs,
        fetchServiceConfigs,
        fetchQuerySchedules,
        fetchFileTransferStatsByTransID,
        fetchFileTransferStatsByProject,
        fetchFileTransferStatsByGroup,
        fetchEICacheList,
        fetchRemarqueEvents,
        fetchErrorLogReport,
        fetchEventsReport,
        fetchRecieveDataReport,
        fetchQueryInfo,
        fetchErrorDetails,
        fetchAPIConfigsFromAllEnv,
        fetchServiceConfigsFromAllEnv,
        fetchTaskSchedulesFromAllEnv,
        fetchEIConenctionsFromAllEnv,
        fetchFileTransfersFromAllEnv,
        fetchfileTransferHistory,
        fetchRemoteExecEvents,
        fetchRemoteProcessList,
        fetchUnifiedEventSummary,
        fetchUnifiedFileSummary,
        fetchBatchProcessSummary,
        fetchJobRunHistory,
        REACT_APP_ENV,
        envColumn1,
        envColumn2,
        openPromoteModal,
        setOpenPromoteModal,
        openMatchModal,
        setOpenMatchModal,
        matchCell,
        matchSelectedFields,
        setMatchSelectedFields,
        matchDirection,
        setMatchDirection,
        handleMatchClickOpen,
        handleMatchClose,
        openDetailForm,
        editable,
        envCell,
        curRow,
        handleClickOpenDetail,
        handleDetailClose,
        onEditClick,
        handleClickOpen,
        handleClose,
      }}
    >
      {children}
    </DataViewContext.Provider>
  );
}
